import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";

// Admin: growth from day one (2026-10-20). Hand-made SVG charts, one series each, tap/hover for values.
const ACCENT = "#3b82f6";
const W = 340, H = 170, PL = 34, PR = 8, PT = 10, PB = 22;

const PERIODS: { key: number; label: string }[] = [
  { key: 30, label: "30 дней" }, { key: 90, label: "90 дней" }, { key: 0, label: "Всё время" },
];
const METRICS: { key: string; label: string; unit?: string }[] = [
  { key: "new_users", label: "Новые" }, { key: "trials", label: "Пробный" },
  { key: "first_search", label: "Первый поиск" }, { key: "payments", label: "Оплаты" },
  { key: "revenue", label: "Выручка", unit: "₽" }, { key: "blocked", label: "Заблокировали" },
];

function dd(day: string) {
  const [, m, d] = day.split("-");
  return `${d}.${m}`;
}

function num(v: number, unit?: string) {
  return `${Math.round(v).toLocaleString("ru-RU")}${unit ? ` ${unit}` : ""}`;
}

function niceMax(v: number) {
  if (v <= 4) return 4;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / p;
  return (n <= 2 ? 2 : n <= 5 ? 5 : 10) * p;
}

function useTap(n: number) {
  const ref = useRef<SVGSVGElement>(null);
  const [idx, setIdx] = useState<number | null>(null);
  const pick = (clientX: number) => {
    const el = ref.current;
    if (!el || n === 0) return;
    const r = el.getBoundingClientRect();
    const x = ((clientX - r.left) / r.width) * W;
    const i = Math.round(((x - PL) / (W - PL - PR)) * (n - 1));
    setIdx(Math.max(0, Math.min(n - 1, n === 1 ? 0 : i)));
  };
  return { ref, idx, setIdx, pick };
}

function Axes({ max, labels, unit }: { max: number; labels: string[]; unit?: string }) {
  const ticks = [0, max / 2, max];
  const pw = W - PL - PR, ph = H - PT - PB;
  return (
    <g>
      {ticks.map((t) => {
        const y = PT + ph - (t / max) * ph;
        return (
          <g key={t}>
            <line x1={PL} x2={W - PR} y1={y} y2={y} stroke="rgba(127,127,127,0.18)" strokeWidth={1} />
            <text x={PL - 4} y={y + 3} textAnchor="end" fontSize="9" fill="var(--tg-theme-hint-color, #888)">
              {unit === "₽" && t >= 1000 ? `${Math.round(t / 1000)}к` : Math.round(t).toLocaleString("ru-RU")}
            </text>
          </g>
        );
      })}
      {labels.length > 0 && [0, Math.floor((labels.length - 1) / 2), labels.length - 1]
        .filter((v, i, a) => a.indexOf(v) === i)
        .map((i) => (
          <text key={i} x={PL + (labels.length === 1 ? pw / 2 : (i / (labels.length - 1)) * pw)} y={H - 6}
                textAnchor={i === 0 ? "start" : i === labels.length - 1 ? "end" : "middle"} fontSize="9"
                fill="var(--tg-theme-hint-color, #888)">{dd(labels[i])}</text>
        ))}
    </g>
  );
}

function LineChart({ days, values }: { days: string[]; values: number[] }) {
  const { ref, idx, setIdx, pick } = useTap(values.length);
  const max = niceMax(Math.max(...values, 1));
  const pw = W - PL - PR, ph = H - PT - PB;
  const x = (i: number) => PL + (values.length === 1 ? pw / 2 : (i / (values.length - 1)) * pw);
  const y = (v: number) => PT + ph - (v / max) * ph;
  const pts = values.map((v, i) => `${x(i)},${y(v)}`).join(" ");
  const area = `${x(0)},${PT + ph} ${pts} ${x(values.length - 1)},${PT + ph}`;
  return (
    <div style={{ position: "relative" }}>
      <svg ref={ref} viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", display: "block", touchAction: "pan-y" }}
           onMouseMove={(e) => pick(e.clientX)} onMouseLeave={() => setIdx(null)} onClick={(e) => pick(e.clientX)}>
        <Axes max={max} labels={days} />
        <polygon points={area} fill={ACCENT} opacity={0.12} />
        <polyline points={pts} fill="none" stroke={ACCENT} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {idx !== null && (
          <g>
            <line x1={x(idx)} x2={x(idx)} y1={PT} y2={PT + ph} stroke="rgba(127,127,127,0.5)" strokeWidth={1} />
            <circle cx={x(idx)} cy={y(values[idx])} r={4} fill={ACCENT} stroke="var(--tg-theme-bg-color, #fff)" strokeWidth={2} />
          </g>
        )}
      </svg>
      {idx !== null && <div style={styles.tip}>{dd(days[idx])}: <b>{num(values[idx])}</b></div>}
    </div>
  );
}

function BarChart({ days, values, unit }: { days: string[]; values: number[]; unit?: string }) {
  const { ref, idx, setIdx, pick } = useTap(values.length);
  const max = niceMax(Math.max(...values, 1));
  const pw = W - PL - PR, ph = H - PT - PB;
  const step = pw / Math.max(values.length, 1);
  const bw = Math.max(1, step - (step > 6 ? 2 : 0.5));
  return (
    <div style={{ position: "relative" }}>
      <svg ref={ref} viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", display: "block", touchAction: "pan-y" }}
           onMouseMove={(e) => pick(e.clientX)} onMouseLeave={() => setIdx(null)} onClick={(e) => pick(e.clientX)}>
        <Axes max={max} labels={days} unit={unit} />
        {values.map((v, i) => {
          const h = (v / max) * ph;
          return v > 0 ? (
            <rect key={i} x={PL + i * step + (step - bw) / 2} y={PT + ph - h} width={bw} height={h}
                  rx={bw > 6 ? 2 : 0} fill={ACCENT} opacity={idx === null || idx === i ? 1 : 0.45} />
          ) : null;
        })}
      </svg>
      {idx !== null && <div style={styles.tip}>{dd(days[idx])}: <b>{num(values[idx], unit)}</b></div>}
    </div>
  );
}

export default function AdminGrowth() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState(30);
  const [metric, setMetric] = useState("new_users");
  const [d, setD] = useState<any>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setD(null);
    setError(false);
    api.get(`/admin/growth?days=${period}`).then((r: any) => setD(r.data)).catch(() => setError(true));
  }, [period]);

  const days: string[] = d ? d.series.map((r: any) => r.day) : [];
  const m = METRICS.find((x) => x.key === metric)!;
  const f = d?.funnel;
  const pct = (a: number, b: number) => (b > 0 ? `${Math.round((a / b) * 100)}%` : "—");

  return (
    <div style={styles.container}>
      <button style={styles.backBtn} onClick={() => navigate("/admin")}>← Админ-панель</button>
      <h1 style={styles.title}>Пользователи</h1>
      <div style={styles.tabs}>
        <button style={{ ...styles.tabBtn, ...styles.tabBtnActive }}>Динамика</button>
        <button style={styles.tabBtn} onClick={() => navigate("/admin/users")}>Список ›</button>
      </div>
      <div style={styles.chips}>
        {PERIODS.map((p) => (
          <button key={p.key} style={{ ...styles.chip, ...(period === p.key ? styles.chipOn : {}) }} onClick={() => setPeriod(p.key)}>{p.label}</button>
        ))}
      </div>

      {error && <div style={styles.hint}>Не удалось загрузить данные</div>}
      {!d && !error && <div style={styles.center}><div style={styles.spinner} /></div>}
      {d && (
        <>
          <div style={styles.tiles}>
            <div style={styles.tile}><div style={styles.tileValue}>{num(d.now.users)}</div><div style={styles.hint}>пользователей, +{num(d.totals.new_users)} за период</div></div>
            <div style={styles.tile}><div style={styles.tileValue}>{num(d.now.paying)}</div><div style={styles.hint}>платят сейчас, пробный идёт у {num(d.now.trial)}</div></div>
            <div style={styles.tile}><div style={styles.tileValue}>{num(d.totals.revenue, "₽")}</div><div style={styles.hint}>выручка за период, оплат {num(d.totals.payments)}</div></div>
          </div>

          <div style={styles.box}>
            <div style={styles.boxTitle}>Пользователей всего</div>
            <div style={styles.hint}>с {dd(d.start)} · нажмите на график, чтобы увидеть число за день</div>
            <LineChart days={days} values={d.series.map((r: any) => r.users_total)} />
          </div>

          <div style={styles.box}>
            <div style={styles.boxTitle}>По дням: {m.label.toLowerCase()} — {num(d.totals[metric], m.unit)} за период</div>
            <div style={{ ...styles.chips, marginBottom: "6px" }}>
              {METRICS.map((x) => (
                <button key={x.key} style={{ ...styles.chipSmall, ...(metric === x.key ? styles.chipOn : {}) }} onClick={() => setMetric(x.key)}>{x.label}</button>
              ))}
            </div>
            <BarChart days={days} values={d.series.map((r: any) => r[metric])} unit={m.unit} />
          </div>

          <div style={styles.box}>
            <div style={styles.boxTitle}>Воронка: пришли за период</div>
            {[["Пришли", f.came], ["Начали пробный", f.trial], ["Создали поиск", f.search], ["Оплатили", f.paid]].map(([label, v]: any) => (
              <div key={label} style={styles.funnelRow}>
                <span style={styles.funnelLabel}>{label}</span>
                <div style={styles.funnelOuter}><div style={{ ...styles.funnelBar, width: `${f.came ? Math.max(2, (v / f.came) * 100) : 0}%` }} /></div>
                <span style={styles.funnelValue}>{num(v)} · {pct(v, f.came)}</span>
              </div>
            ))}
          </div>

          {d.sources.length > 0 && (
            <div style={styles.box}>
              <div style={styles.boxTitle}>Откуда пришли за период</div>
              {d.sources.map((s: any) => (
                <div key={s.source} style={styles.row}>
                  <span style={styles.label}>{s.source}</span>
                  <span style={styles.value}>{num(s.users)} чел.{s.paid ? ` · оплатили ${s.paid}` : ""}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: "16px", maxWidth: "480px", margin: "0 auto" },
  backBtn: { background: "none", border: "none", color: "var(--tg-theme-link-color, #2481cc)", fontSize: "14px", cursor: "pointer", padding: 0, marginBottom: "12px" },
  title: { fontSize: "22px", fontWeight: 700, margin: "0 0 10px 0", color: "var(--tg-theme-text-color, #000)" },
  tabs: { display: "flex", gap: "8px", marginBottom: "12px" },
  tabBtn: { flex: 1, padding: "8px", borderRadius: "10px", border: "none", backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", color: "var(--tg-theme-text-color, #000)", fontSize: "13px", cursor: "pointer" },
  tabBtnActive: { backgroundColor: "var(--tg-theme-button-color, #2481cc)", color: "var(--tg-theme-button-text-color, #fff)" },
  center: { display: "flex", justifyContent: "center", padding: "40px 0" },
  spinner: { width: "28px", height: "28px", border: "3px solid #e5e7eb", borderTop: "3px solid #3b82f6", borderRadius: "50%", animation: "spin 1s linear infinite" },
  hint: { fontSize: "12px", color: "var(--tg-theme-hint-color, #666)" },
  chips: { display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "12px" },
  chip: { padding: "6px 12px", borderRadius: "16px", border: "none", backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", color: "var(--tg-theme-text-color, #000)", fontSize: "13px", cursor: "pointer" },
  chipSmall: { padding: "4px 10px", borderRadius: "14px", border: "none", backgroundColor: "rgba(127,127,127,0.12)", color: "var(--tg-theme-text-color, #000)", fontSize: "12px", cursor: "pointer" },
  chipOn: { backgroundColor: "var(--tg-theme-button-color, #2481cc)", color: "var(--tg-theme-button-text-color, #fff)" },
  tiles: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", marginBottom: "10px" },
  tile: { backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", borderRadius: "12px", padding: "10px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
  tileValue: { fontSize: "18px", fontWeight: 800, color: "var(--tg-theme-text-color, #000)" },
  box: { backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", borderRadius: "12px", padding: "12px", marginBottom: "10px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
  boxTitle: { fontSize: "13px", fontWeight: 700, color: "var(--tg-theme-text-color, #000)", marginBottom: "4px" },
  tip: { position: "absolute", top: 0, right: 0, fontSize: "12px", padding: "3px 8px", borderRadius: "8px", backgroundColor: "var(--tg-theme-bg-color, #fff)", color: "var(--tg-theme-text-color, #000)", boxShadow: "0 1px 4px rgba(0,0,0,0.15)" },
  funnelRow: { display: "flex", alignItems: "center", gap: "8px", padding: "4px 0", fontSize: "12px" },
  funnelLabel: { width: "96px", color: "var(--tg-theme-hint-color, #666)" },
  funnelOuter: { flex: 1, height: "10px", borderRadius: "5px", backgroundColor: "rgba(127,127,127,0.15)" },
  funnelBar: { height: "10px", borderRadius: "5px", backgroundColor: ACCENT },
  funnelValue: { width: "78px", textAlign: "right", color: "var(--tg-theme-text-color, #000)" },
  row: { display: "flex", justifyContent: "space-between", gap: "12px", padding: "3px 0", fontSize: "13px" },
  label: { color: "var(--tg-theme-hint-color, #666)" },
  value: { color: "var(--tg-theme-text-color, #000)", fontWeight: 600, textAlign: "right" },
};
